import type { RouteDef } from "@superblocksteam/shared";

import type { UrlDataState } from "legacy/reducers/entityReducers/appReducer";
import type { APP_MODE } from "legacy/reducers/types";
import type { getSystemQueryParams } from "legacy/utils/queryParams";
import { parseValidationBoolean } from "legacy/workers/validators/parseValidationBoolean";

import { getLinkProperties } from "../LinkWidget/utils";
import { MenuChildrenType, MenuItemType } from "./types";
import type { ComponentMenuItem, ManualMenuItem } from "./types";
import { getLinkPropsFromMenuItem } from "./utils";

type Context = {
  routes: { [id: string]: RouteDef };
  currentPageUrlState: UrlDataState;
  systemQueryParams: ReturnType<typeof getSystemQueryParams>;
  appMode: APP_MODE | undefined;
};

export function transformItem(
  item: ManualMenuItem,
  context: Context,
): ComponentMenuItem {
  if (item.type === MenuItemType.Divider) {
    return item;
  }

  if (item.childrenType === MenuChildrenType.Manual) {
    const children =
      item.manualChildren && item.manualChildren.length > 0
        ? item.manualChildren.map((child) => transformItem(child, context))
        : undefined;

    /**
     * Nested items don't support our native VALIDATION_TYPES yet.
     * Those validations not only validate values, but also parse them.
     * So we need to do the parsing here, until we properly support VALIDATION_TYPES in nested items.
     *
     * Biggest drawback here is we don't support showing a validation message in case of error.
     */
    const isVisible = parseValidationBoolean(item.isVisible).parsed;
    const isDisabled = parseValidationBoolean(item.isDisabled).parsed;

    switch (item.type) {
      case MenuItemType.Button: {
        return {
          type: item.type,
          label: item.label,
          icon: item.icon,
          iconPosition: item.iconPosition,
          openOn: item.openOn,
          isVisible,
          isDisabled,
          onClick: item.onClick,
          children,
        };
      }
      case MenuItemType.Link: {
        const { url, urlForInternalNavigation, hasMissingRouteParams } =
          getLinkProperties(getLinkPropsFromMenuItem(item), context);

        return {
          type: item.type,
          label: item.label,
          href: url,
          urlForInternalNavigation,
          icon: item.icon,
          iconPosition: item.iconPosition,
          targetBlank: item.targetBlank,
          children,
          hasMissingRouteParams,
          isVisible,
          isDisabled,
        };
      }
      default: {
        throw new Error(`Invalid menu item type for ${item}`);
      }
    }
  } else {
    throw new Error(`Non-manual children types are implemented yet`);
  }
}
